// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Field-level tree comparison (spec §6.3: "Produce a **field-level list** of
 * what changed (`width: 0.2 → 0.25`, `layer: F.Cu → B.Cu`, `net: SDA → SCL`)").
 */

import { atoms, isNode, serializeCompact, type Node } from '../sexp/index.ts';

export interface FieldChange {
  /** Dotted path, e.g. `stroke.width` or `pad[1].at`. */
  path: string;
  /** Absent when the field only exists on the B side. */
  before?: string;
  /** Absent when the field only exists on the A side. */
  after?: string;
}

export function formatFieldChange(c: FieldChange): string {
  if (c.before === undefined) return `${c.path}: added ${c.after}`;
  if (c.after === undefined) return `${c.path}: removed ${c.before}`;
  return `${c.path}: ${c.before} → ${c.after}`;
}

const MAX_CHANGES = 200;

/**
 * Compare two nodes and describe the differences.
 *
 * Children are matched by token name and then positionally within that name,
 * which is how KiCad's own format is structured (repeated `pad`, `fp_line`,
 * `xy` children). Output is capped so a wholesale rewrite cannot produce an
 * unbounded list; the cap is reported by the caller.
 */
export function diffFields(a: Node, b: Node, rootPath = ''): FieldChange[] {
  const out: FieldChange[] = [];
  compare(a, b, rootPath, out);
  return out;
}

function compare(a: Node, b: Node, path: string, out: FieldChange[]): void {
  if (out.length >= MAX_CHANGES) return;

  // Positional atoms of this node.
  const aa = atoms(a);
  const ba = atoms(b);
  const atomCount = Math.max(aa.length, ba.length);
  for (let i = 0; i < atomCount; i++) {
    const av = aa[i]?.value;
    const bv = ba[i]?.value;
    if (av === bv) continue;
    const label = atomCount > 1 ? `${path || a.name}[${i}]` : path || a.name;
    push(out, { path: label, ...(av !== undefined ? { before: av } : {}), ...(bv !== undefined ? { after: bv } : {}) });
    if (out.length >= MAX_CHANGES) return;
  }

  // Child nodes, grouped by token name.
  const names: string[] = [];
  const seen = new Set<string>();
  for (const c of [...a.children, ...b.children]) {
    if (!isNode(c) || seen.has(c.name)) continue;
    seen.add(c.name);
    names.push(c.name);
  }

  for (const name of names) {
    const al = a.children.filter((c): c is Node => isNode(c) && c.name === name);
    const bl = b.children.filter((c): c is Node => isNode(c) && c.name === name);
    const count = Math.max(al.length, bl.length);
    for (let i = 0; i < count; i++) {
      const an = al[i];
      const bn = bl[i];
      const childPath = joinPath(path, count > 1 ? `${name}[${i}]` : name);
      if (an && bn) {
        compare(an, bn, childPath, out);
      } else if (an) {
        push(out, { path: childPath, before: serializeCompact(an) });
      } else if (bn) {
        push(out, { path: childPath, after: serializeCompact(bn) });
      }
      if (out.length >= MAX_CHANGES) return;
    }
  }
}

function joinPath(prefix: string, part: string): string {
  return prefix === '' ? part : `${prefix}.${part}`;
}

function push(out: FieldChange[], change: FieldChange): void {
  if (out.length < MAX_CHANGES) out.push(change);
}

export const FIELD_CHANGE_CAP = MAX_CHANGES;
